import { AutocompleteWithLabel } from '@adotel/components'
import {
  LazyQueryExecFunction,
  OperationVariables,
  useQuery
} from '@apollo/client'
import { Button, CircularProgress } from '@material-ui/core'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'

import { ACCOMMODATIONS_QUERY } from '../../graphql/accommodation'
import { CHANNEL_TYPES_QUERY } from '../../graphql/channelTypes'
import { getTranslate } from '../../localization'
import { Accommodation } from '../../types/accommodation'
import {
  BookingPermission,
  BookingPermissionTenantInput
} from '../../types/bookingPermission'
import { ChannelTypeDetails } from '../../types/channelType'
import { BookingActionTypes } from './BookingPermissionFilterOptions'
import useStyles from './styles/BookingPermissionFilters'

type BookingPermissionFiltersProps = {
  input: BookingPermissionTenantInput
  setInput: React.Dispatch<React.SetStateAction<BookingPermissionTenantInput>>
  getBookingPermissions: LazyQueryExecFunction<
    {
      tenantBookingPermission: BookingPermission[]
    },
    OperationVariables
  >
  loadingForBookingPermissions: boolean
}

const BookingPermissionFilters = ({
  input,
  setInput,
  getBookingPermissions,
  loadingForBookingPermissions
}: BookingPermissionFiltersProps) => {
  const classes = useStyles()
  const params = useParams<{ tenantId: string }>()

  const [autoCompleteForAccommodations, setAutoCompleteForAccommodations] =
    useState<any[]>([])
  const [autoCompleteForChannelType, setAutoCompleteForChannelType] =
    useState<any>(null)
  const [autoCompleteForActionType, setAutoCompleteForActionType] =
    useState<any>(null)

  const { data: accommodationsData, loading: loadingForAccommodation } =
    useQuery<{
      accommodations: Accommodation[]
    }>(ACCOMMODATIONS_QUERY, {
      notifyOnNetworkStatusChange: true,
      variables: {
        tenantId: params.tenantId
      }
    })

  const { data: channelTypes, loading: loadingForChannelTypes } = useQuery<{
    ChannelTypes: ChannelTypeDetails[]
  }>(CHANNEL_TYPES_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const onApplyClick = () => {
    getBookingPermissions({
      variables: {
        input: input
      }
    })
  }

  return (
    <div className={classes.root}>
      <div className={classes.selectFields}>
        <AutocompleteWithLabel
          value={autoCompleteForAccommodations}
          showSelectAll
          multiple
          limitTags={1}
          fullWidth
          getOptionLabel={(option) => option.label || ''}
          label={getTranslate('Accommodation')}
          inputSize="small"
          size="small"
          options={
            accommodationsData
              ? accommodationsData?.accommodations.map((item) => ({
                  label: item.basicDetails.name,
                  value: item.basicDetails.propertyCode || '',
                  code: item.basicDetails?.propertyCode || '',
                  id: item.basicDetails.accommodationID || ''
                }))
              : []
          }
          onChange={(_, v: any) => {
            if (v.length > 0) {
              const tempSelectedAccs: any[] = []
              const ids: string[] = []

              v.forEach((item: any) => {
                tempSelectedAccs.push({
                  label: item.label,
                  value: item.value,
                  code: item.code,
                  id: item.id
                })
                ids.push(item.id)
              })

              setInput({ ...input, accommodationIds: ids })
              setAutoCompleteForAccommodations(tempSelectedAccs)
            } else {
              setInput({ ...input, accommodationIds: [] })
              setAutoCompleteForAccommodations([])
            }
          }}
          loading={loadingForAccommodation}
          loadingText={getTranslate('Loading') + '...'}
        />
        <AutocompleteWithLabel
          fullWidth
          value={autoCompleteForChannelType ? autoCompleteForChannelType : null}
          label={getTranslate('Channel Type')}
          onChange={(_, v: any) => {
            if (v) {
              setInput({ ...input, channelType: v.value })
              setAutoCompleteForChannelType(v)
            } else {
              setInput({ ...input, channelType: undefined })
              setAutoCompleteForChannelType(null)
            }
          }}
          getOptionLabel={(option) => option?.label || ''}
          getOptionSelected={(option, value) => option.value === value.value}
          options={
            channelTypes?.ChannelTypes
              ? channelTypes.ChannelTypes.map((item) => ({
                  label: item.Name as string,
                  value: item.Name as string
                }))
              : []
          }
          size="small"
          inputSize="small"
          includeInputInList
          loading={loadingForChannelTypes}
          loadingText={getTranslate('Loading') + '...'}
        />
        <AutocompleteWithLabel
          fullWidth
          value={autoCompleteForActionType ? autoCompleteForActionType : null}
          label={getTranslate('Action Type')}
          onChange={(_, v: any) => {
            if (v) {
              setInput({ ...input, actionType: v.value })
              setAutoCompleteForActionType(v)
            } else {
              setInput({ ...input, actionType: undefined })
              setAutoCompleteForActionType(null)
            }
          }}
          getOptionLabel={(option) => option?.label || ''}
          getOptionSelected={(option, value) => option.value === value.value}
          options={
            BookingActionTypes
              ? BookingActionTypes.map((item) => ({
                  label: getTranslate(item),
                  value: item
                }))
              : []
          }
          size="small"
          inputSize="small"
          includeInputInList
          loadingText={getTranslate('Loading') + '...'}
        />
      </div>
      <div className={classes.ButtonsContainer}>
        <Button
          className={classes.resetButton}
          variant="text"
          color="secondary"
          onClick={() => {
            setAutoCompleteForAccommodations([])
            setAutoCompleteForChannelType(null)
            setAutoCompleteForActionType(null)
            setInput({
              accommodationIds: [],
              actionType: undefined,
              channelType: undefined
            })
          }}>
          {getTranslate('Reset')}
        </Button>
        <Button
          className={classes.applyButton}
          variant="contained"
          color="primary"
          disabled={
            input.accommodationIds.length < 1 ||
            !input.channelType ||
            !input.actionType ||
            loadingForBookingPermissions
          }
          onClick={() => {
            onApplyClick()
          }}>
          {getTranslate('Apply')}
          {loadingForBookingPermissions && (
            <CircularProgress
              className={classes.circularProgress}
              size={16}
              color="primary"
            />
          )}
        </Button>
      </div>
    </div>
  )
}

export default BookingPermissionFilters
